<template>
    <div class="absolute h-full w-full">
        <!-- Forward video -->
        <video
            ref="camelVideo"
            class="absolute top-0 z-3 h-full w-auto max-w-none -translate-x-1/2 object-cover left-1/2"
            playsinline="true"
            muted="true"
            preload="auto"
            @loadeddata="onVideoLoaded"
            @playing="onPlaying"
            @timeupdate="onTimeUpdate"
            @error="onVideoError"
            @stalled="onVideoStalled"
            @abort="onVideoAbort"
            :style="{ visibility: isPlayingReverse ? 'hidden' : 'visible' }"
        >
            <source
                :src="camelraceAssets.getBlobUrl(camel.color)"
                :type="camelraceAssets.getEncoding(camel.color)"
            />
        </video>

        <!-- Reverse video -->
        <video
            ref="camelVideoReverse"
            class="absolute top-0 z-3 h-full w-auto max-w-none -translate-x-1/2 object-cover left-1/2"
            playsinline="true"
            muted="true"
            preload="auto"
            @loadeddata="onReverseVideoLoaded"
            @playing="onPlaying"
            @timeupdate="onReverseTimeUpdate"
            @error="onReverseVideoError"
            @stalled="onReverseVideoStalled"
            @abort="onReverseVideoAbort"
            :style="{ visibility: isPlayingReverse ? 'visible' : 'hidden' }"
        >
            <source
                :src="camelraceAssets.getBlobUrl(camel.color + '_reverse')"
                :type="camelraceAssets.getEncoding(camel.color + '_reverse')"
            />
        </video>
        <CamelCard :camel="camel" :reachedStep="reachedStep" />
    </div>
</template>

<script>
import { camelraceAssets } from '@/stores/camelraceAssets'
import { useErrorReporting } from '@/composables/useErrorReporting'
import CamelCard from './CamelCard.vue'

export default {
    name: 'CamelVideo',
    setup() {
        const errorReporting = useErrorReporting()
        return { errorReporting }
    },
    emits: ['warmed'],
    props: {
        camel: {
            type: Object,
            required: true,
            validator(value) {
                return value && typeof value.color === 'string' && typeof value.step === 'number'
            },
        },
    },
    data() {
        return {
            videoDuration: 0,
            reverseVideoDuration: 0,
            stepDuration: 0,
            reverseStepDuration: 0,
            isPlaying: false,
            isPlayingReverse: false,
            targetTime: 0,
            animationFrameId: null,
            preciseCheckingActive: false,
            currentStep: 0,
            isTransitioning: false,
            reachedStep: 0,
            hasPlayed: false,
            playRetries: 0,
            MAX_PLAY_RETRIES: 5,
            PLAY_RETRY_DELAY_MS: 120,
            WARMUP_PLAYBACK_RATE: 4,
            WARMUP_FAILSAFE_MS: 15000,
            warmStarted: false,
            warmed: false,
        }
    },
    computed: {
        camelraceAssets() {
            return camelraceAssets()
        },
    },
    components: {
        CamelCard,
    },
    watch: {
        'camel.step': {
            handler(newStep, oldStep) {
                if (this.warmStarted && !this.warmed) {
                    return
                }
                if (this.videoDuration > 0 && this.reverseVideoDuration > 0) {
                    this.handleStepChange(newStep, oldStep || this.currentStep)
                }
            },
            immediate: true,
        },
    },
    mounted() {
        setTimeout(() => {
            if (this.warmStarted && !this.warmed) return
            this.positionVideoForStep(this.camel.step)
        }, 2000)

        setTimeout(() => {
            if (!this.warmed) {
                this.reportVideoIssue('camel warm failsafe', {})
                this.finalizeWarm()
            }
        }, this.WARMUP_FAILSAFE_MS)
    },
    beforeUnmount() {
        this.stopPreciseChecking()
    },
    methods: {
        reportVideoIssue(message, extra) {
            this.errorReporting.captureMessage(message, {
                level: 'error',
                tags: { feature: 'camelrace', camelColor: this.camel.color },
                extra,
            })
        },
        onVideoError(e) {
            const v = e.target
            const err = v && v.error
            this.reportVideoIssue('camel video error', {
                code: err && err.code,
                message: err && err.message,
                networkState: v && v.networkState,
                readyState: v && v.readyState,
                src: v && v.currentSrc,
            })
        },
        onVideoStalled() {
            this.reportVideoIssue('camel video stalled', {})
        },
        onVideoAbort() {
            this.reportVideoIssue('camel video abort', {})
        },
        onReverseVideoError(e) {
            const v = e.target
            const err = v && v.error
            this.reportVideoIssue('camel reverse video error', {
                code: err && err.code,
                message: err && err.message,
                networkState: v && v.networkState,
                readyState: v && v.readyState,
                src: v && v.currentSrc,
            })
        },
        onReverseVideoStalled() {
            this.reportVideoIssue('camel reverse video stalled', {})
        },
        onReverseVideoAbort() {
            this.reportVideoIssue('camel reverse video abort', {})
        },
        onVideoLoaded() {
            const video = this.$refs.camelVideo
            if (!video) {
                this.reportVideoIssue('camel onVideoLoaded but no ref', {})
                return
            }
            if (!video.duration || isNaN(video.duration)) {
                this.reportVideoIssue('camel video bad duration', { duration: video.duration })
            }
            this.videoDuration = video.duration
            this.stepDuration = this.videoDuration / 10

            if (this.camel.step >= 0) {
                this.currentStep = this.camel.step
                video.currentTime = this.camel.step * this.stepDuration
            }

            this.checkIfBothVideosLoaded()
        },

        onReverseVideoLoaded() {
            const reverseVideo = this.$refs.camelVideoReverse
            if (!reverseVideo) {
                this.reportVideoIssue('camel onReverseVideoLoaded but no ref', {})
                return
            }
            if (!reverseVideo.duration || isNaN(reverseVideo.duration)) {
                this.reportVideoIssue('camel reverse video bad duration', {
                    duration: reverseVideo.duration,
                })
            }
            this.reverseVideoDuration = reverseVideo.duration
            this.reverseStepDuration = this.reverseVideoDuration / 10

            if (this.camel.step >= 0) {
                reverseVideo.currentTime = (10 - this.camel.step) * this.reverseStepDuration
            }

            this.checkIfBothVideosLoaded()
        },

        checkIfBothVideosLoaded() {
            if (this.videoDuration > 0 && this.reverseVideoDuration > 0) {
                this.warmClips()
            }
        },

        
        warmClips() {
            if (this.warmStarted) return
            this.warmStarted = true

            const forward = this.$refs.camelVideo
            const reverse = this.$refs.camelVideoReverse
            const stagger = (this.camel.column || 0) * 250

            setTimeout(() => {
                // Warm forward then reverse sequentially (2 decoders per camel at
                // a time rather than 4).
                this.warmOne(forward)
                    .then(() => this.warmOne(reverse))
                    .finally(() => this.finalizeWarm())
            }, stagger)
        },

        finalizeWarm() {
            if (this.warmed) return
            this.warmed = true
            // Restore normal speed in case the failsafe fired mid-warm.
            const forward = this.$refs.camelVideo
            const reverse = this.$refs.camelVideoReverse
            if (forward) forward.playbackRate = 1
            if (reverse) reverse.playbackRate = 1
            this.currentStep = this.clampStep(this.camel.step)
            this.positionVideoForStep(this.camel.step)
            this.$emit('warmed', this.camel.color)
        },

        clampStep(step) {
            return Math.max(0, Math.min(10, step))
        },

        warmOne(video) {
            return new Promise((resolve) => {
                if (!video || !video.duration || isNaN(video.duration)) {
                    resolve()
                    return
                }

                let settled = false
                const finish = () => {
                    if (settled) return
                    settled = true
                    video.removeEventListener('ended', onEnd)
                    video.removeEventListener('timeupdate', onTick)
                    try {
                        video.pause()
                        video.playbackRate = 1
                    } catch {
                        // ignore — element may be detached
                    }
                    resolve()
                }
                const onEnd = () => finish()
                const onTick = () => {
                    // Some platforms don't reliably fire 'ended' for muted
                    // programmatic playback — settle as we reach the end.
                    if (video.duration && video.currentTime >= video.duration - 0.05) {
                        finish()
                    }
                }

                video.addEventListener('ended', onEnd)
                video.addEventListener('timeupdate', onTick)
                // Safety net: never let warm-up hang readiness, even if the clip
                // stalls or events never fire. Scale with the warm-up rate.
                setTimeout(finish, (video.duration * 1000) / this.WARMUP_PLAYBACK_RATE + 4000)

                try {
                    video.currentTime = 0
                    video.muted = true
                    video.playbackRate = this.WARMUP_PLAYBACK_RATE
                    const playPromise = video.play()
                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch((err) => {
                            // Can't warm (e.g. Low Power Mode) — give up gracefully;
                            // playback path will retry/snap as usual at move time.
                            this.reportVideoIssue('camel warm play fail', {
                                name: err && err.name,
                                message: err && err.message,
                            })
                            finish()
                        })
                    }
                } catch {
                    finish()
                }
            })
        },

        handleStepChange(newStep, oldStep) {
            const clampedNewStep = Math.max(0, Math.min(10, newStep))
            const clampedOldStep = Math.max(0, Math.min(10, oldStep))
            this.reachedStep = null

            if (
                this.isTransitioning &&
                this.shouldContinueInSameDirection(clampedNewStep, clampedOldStep)
            ) {
                this.targetTime =
                    clampedNewStep < clampedOldStep
                        ? (10 - clampedNewStep) * this.reverseStepDuration
                        : clampedNewStep * this.stepDuration
            } else {
                if (clampedNewStep < clampedOldStep) {
                    this.playToStepReverse(clampedNewStep, clampedOldStep)
                } else if (clampedNewStep > clampedOldStep) {
                    this.playToStepForward(clampedNewStep, clampedOldStep)
                }
            }

            this.currentStep = clampedNewStep
        },

        shouldContinueInSameDirection(newStep, oldStep) {
            if (!this.isPlayingReverse && this.isPlaying && newStep > oldStep) {
                return true
            }
            if (this.isPlayingReverse && this.isPlaying && newStep < oldStep) {
                return true
            }
            return false
        },

        positionVideoForStep(step) {
            const video = this.$refs.camelVideo
            const reverseVideo = this.$refs.camelVideoReverse
            if (!video || !reverseVideo) return

            this.isPlayingReverse = false

            // Reverse clip is hidden and already primed by warm-up — seek directly.
            reverseVideo.currentTime = (10 - step) * this.reverseStepDuration

            // Safari won't paint a seeked frame until the element has played at
            // least once. Warm-up leaves the playhead at the END of the clip, and
            // currentTime assignment is ASYNCHRONOUS — so we must prime with a
            // play/pause first and seek LAST. Seeking last guarantees the playhead
            // (and the painted frame) settles on the target step instead of being
            // clobbered by a play() that resumes from the end.
            const seekToStep = () => {
                video.currentTime = step * this.stepDuration
            }
            video
                .play()
                .then(() => {
                    video.pause()
                    seekToStep()
                })
                .catch((err) => {
                    // Couldn't prime (e.g. Low Power Mode) — seek anyway so the
                    // camel at least lands on the right frame.
                    seekToStep()
                    this.reportVideoIssue('camel play fail', {
                        name: err && err.name,
                        message: err && err.message,
                    })
                })
        },

        playToStepForward(newStep, oldStep) {
            const video = this.$refs.camelVideo
            const reverseVideo = this.$refs.camelVideoReverse
            if (!video || !reverseVideo) return

            this.stopPreciseChecking()
            this.isTransitioning = true

            this.targetTime = newStep * this.stepDuration

            if (this.isPlayingReverse) {
                reverseVideo.pause()

                const currentStep = Math.round(
                    10 - reverseVideo.currentTime / this.reverseStepDuration,
                )
                video.currentTime = currentStep * this.stepDuration

                setTimeout(() => {
                    this.isPlayingReverse = false
                    this.playVideoToTarget(video)
                }, 50)
            } else if (this.isPlaying) {
                this.playVideoToTarget(video)
            } else {
                video.currentTime = oldStep * this.stepDuration
                this.playVideoToTarget(video)
            }
        },

        playToStepReverse(newStep, oldStep) {
            const video = this.$refs.camelVideo
            const reverseVideo = this.$refs.camelVideoReverse
            if (!video || !reverseVideo) return

            this.stopPreciseChecking()
            this.isTransitioning = true

            this.targetTime = (10 - newStep) * this.reverseStepDuration

            if (!this.isPlayingReverse && this.isPlaying) {
                video.pause()

                reverseVideo.currentTime = (10 - oldStep) * this.reverseStepDuration

                setTimeout(() => {
                    this.isPlayingReverse = true
                    this.playVideoToTarget(reverseVideo)
                }, 50)
            } else if (this.isPlayingReverse && this.isPlaying) {
                this.playVideoToTarget(reverseVideo)
            } else {
                reverseVideo.currentTime = (10 - oldStep) * this.reverseStepDuration
                setTimeout(() => {
                    this.isPlayingReverse = true
                    this.playVideoToTarget(reverseVideo)
                }, 50)
            }
        },

        playVideoToTarget(video) {
            if (this.targetTime > video.currentTime) {
                this.isPlaying = true
                this.startPreciseChecking()

                const playPromise = video.play()
                if (playPromise && typeof playPromise.catch === 'function') {
                    playPromise.catch((err) => {
                        this.reportVideoIssue('camel play fail', {
                            name: err && err.name,
                            message: err && err.message,
                            hasPlayed: this.hasPlayed,
                        })
                        if (this.hasPlayed) {
                            // The device CAN play (it has before), so this is a
                            // transient rejection — e.g. an AbortError from a
                            // rapid pause/play, or a buffering hiccup. Retry and
                            // let it play through to the target instead of
                            // teleporting to the end.
                            this.retryPlay(video)
                        } else {
                            // Never played: likely a device/mode that blocks
                            // playback entirely (e.g. iOS Low Power Mode). Snap
                            // so the race still resolves instead of spinning the
                            // precise checker forever.
                            this.snapToTarget(video)
                        }
                    })
                }
            } else {
                video.currentTime = this.targetTime
                video.pause()
                this.isPlaying = false
                this.isTransitioning = false
                this.reachedStep = this.currentStep
            }
        },

        snapToTarget(video) {
            this.stopPreciseChecking()
            try {
                video.currentTime = this.targetTime
                video.pause()
            } catch (e) {
                this.reportVideoIssue('camel snapToTarget fail', {
                    name: e && e.name,
                    message: e && e.message,
                })
            }
            this.isPlaying = false
            this.isTransitioning = false
            this.reachedStep = this.currentStep
        },

        onPlaying() {
            // Playback actually started — remember the device can play, and
            // clear any retry budget consumed by an earlier hiccup.
            this.hasPlayed = true
            this.playRetries = 0
        },

        retryPlay(video) {
            if (this.playRetries >= this.MAX_PLAY_RETRIES) {
                // Exhausted retries — fall back to snapping so the camel still
                // reaches its destination rather than stalling indefinitely.
                this.reportVideoIssue('camel play retry exhausted', {
                    attempts: this.playRetries,
                })
                this.snapToTarget(video)
                return
            }

            this.playRetries++
            setTimeout(() => {
                // A newer transition may have superseded this one, or it may
                // already have settled — don't fight it.
                if (!this.isPlaying) return

                const playPromise = video.play()
                if (playPromise && typeof playPromise.catch === 'function') {
                    playPromise.catch((err) => {
                        this.reportVideoIssue('camel play retry fail', {
                            name: err && err.name,
                            message: err && err.message,
                            attempt: this.playRetries,
                        })
                        this.retryPlay(video)
                    })
                }
            }, this.PLAY_RETRY_DELAY_MS)
        },

        onTimeUpdate(e) {
            if (this.isPlayingReverse) return

            const video = e.target || e.srcElement
            if (!video || !this.isPlaying) return

            const timeUntilTarget = this.targetTime - video.currentTime

            if (timeUntilTarget <= 0.5 && timeUntilTarget > 0 && !this.preciseCheckingActive) {
                this.startPreciseChecking()
            } else if (timeUntilTarget <= 0) {
                video.pause()
                this.isPlaying = false
                this.isTransitioning = false
                this.reachedStep = this.currentStep
                this.stopPreciseChecking()
            }
        },

        onReverseTimeUpdate(e) {
            if (!this.isPlayingReverse) return

            const reverseVideo = e.target || e.srcElement
            if (!reverseVideo || !this.isPlaying) return

            const timeUntilTarget = this.targetTime - reverseVideo.currentTime

            if (timeUntilTarget <= 0.5 && timeUntilTarget > 0 && !this.preciseCheckingActive) {
                this.startPreciseChecking()
            } else if (timeUntilTarget <= 0) {
                reverseVideo.pause()
                this.isPlaying = false
                this.isTransitioning = false
                this.reachedStep = this.currentStep
                this.stopPreciseChecking()
            }
        },

        startPreciseChecking() {
            if (this.preciseCheckingActive) return

            this.preciseCheckingActive = true
            this.checkVideoProgress()
        },

        stopPreciseChecking() {
            this.preciseCheckingActive = false
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId)
                this.animationFrameId = null
            }
        },

        checkVideoProgress() {
            if (!this.preciseCheckingActive) return

            const activeVideo = this.isPlayingReverse
                ? this.$refs.camelVideoReverse
                : this.$refs.camelVideo
            if (!activeVideo || !this.isPlaying) {
                this.stopPreciseChecking()
                return
            }

            if (activeVideo.currentTime >= this.targetTime) {
                activeVideo.pause()
                activeVideo.currentTime = this.targetTime
                this.isPlaying = false
                this.isTransitioning = false
                this.reachedStep = this.currentStep
                this.stopPreciseChecking()
                return
            }

            this.animationFrameId = requestAnimationFrame(() => this.checkVideoProgress())
        },
    },
}
</script>
